import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

const TOOLTIP_GAP_PX = 4;
// Room a wrapped message needs below the target; with less, the tooltip opens above it instead.
const TOOLTIP_MIN_SPACE_BELOW_PX = 160;
// Tallest an interactive tooltip grows before it scrolls; set here, not in CSS, so placement can use it.
const INTERACTIVE_MAX_HEIGHT_PX = 220;
// Time the pointer has to cross the gap from the target into an interactive tooltip before it closes.
const INTERACTIVE_HIDE_DELAY_MS = 150;
// How far an arrow key scrolls an interactive tooltip while its target has focus.
const KEY_SCROLL_STEP_PX = 40;

/**
 * Show/hide handlers (hover or focus) plus a tooltip rendered through a portal, so no scrolling
 * container can clip it. An interactive tooltip stays open while the pointer is over it, so long
 * content can be scrolled (with the arrow keys too, while the target has focus), and is never
 * taller than the space on the side it opens on.
 */
export default function useHoverTooltip(content, {interactive = false} = {}) {
    const [placement, setPlacement] = useState(null);
    const hideTimer = useRef(null);
    const panel = useRef(null);

    const cancelHide = () => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
            hideTimer.current = null;
        }
    };
    useEffect(() => cancelHide, []);

    const show = event => {
        cancelHide();
        const rect = event.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - TOOLTIP_GAP_PX;
        const spaceAbove = rect.top - TOOLTIP_GAP_PX;
        const needed = interactive ? INTERACTIVE_MAX_HEIGHT_PX : TOOLTIP_MIN_SPACE_BELOW_PX;
        // Below when it fits there; otherwise whichever side has more room.
        const below = spaceBelow >= needed || spaceBelow >= spaceAbove;
        const vertical = below
            ? {top: rect.bottom + TOOLTIP_GAP_PX}
            : {bottom: window.innerHeight - rect.top + TOOLTIP_GAP_PX};
        const horizontal = rect.left > window.innerWidth / 2
            ? {right: window.innerWidth - rect.right}
            : {left: rect.left};
        const maxHeight = interactive
            ? Math.max(0, Math.min(INTERACTIVE_MAX_HEIGHT_PX, below ? spaceBelow : spaceAbove))
            : undefined;
        setPlacement({...vertical, ...horizontal, maxHeight});
    };
    const hide = () => {
        if (!interactive) {
            setPlacement(null);
            return;
        }
        cancelHide();
        hideTimer.current = setTimeout(() => setPlacement(null), INTERACTIVE_HIDE_DELAY_MS);
    };
    const scrollWithKeys = event => {
        const step = {ArrowDown: KEY_SCROLL_STEP_PX, ArrowUp: -KEY_SCROLL_STEP_PX}[event.key];
        if (!interactive || step === undefined || !panel.current) {
            return;
        }
        event.preventDefault();
        panel.current.scrollTop += step;
    };

    const tooltip = placement && createPortal(
        <div className={interactive ? "ellipsisTextTooltip ellipsisTextTooltipInteractive" : "ellipsisTextTooltip"}
             role="tooltip" style={placement} ref={panel}
             onMouseEnter={interactive ? cancelHide : undefined} onMouseLeave={interactive ? hide : undefined}>
            {content}
        </div>,
        document.body
    );
    return {show, hide, scrollWithKeys, tooltip};
}
